import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, InputAdornment,
  Button, Menu, MenuItem, Chip, Divider, CircularProgress,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import { ArrowDown2, SearchNormal1 } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { TaskStatusTab, WORK_STATUS_COLORS } from '../../utils/task.utils';

export interface StatusTabItem {
  key: TaskStatusTab;
  label: string;
  count: number;
}

export interface FilterGroup {
  key: string;
  label: string;
  value: string;
  allLabel: string;
  options: { id: string; label: string }[];
  onChange: (value: string) => void;
  loading?: boolean;
  hidden?: boolean;
}

export interface ActiveFilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

interface TasksToolbarProps {
  searchValue: string;
  searchPlaceholder: string;
  onSearchChange: (value: string) => void;
  filterGroups: FilterGroup[];
  activeChips: ActiveFilterChip[];
  onClearAll: () => void;
  filteredCount?: number;
  totalCount?: number;
  statusTabs?: StatusTabItem[];
  activeTab?: TaskStatusTab;
  onTabChange?: (tab: TaskStatusTab) => void;
  sortLabel?: string;
  onSortToggle?: () => void;
  clearFiltersLabel?: string;
  showingCountLabel?: string;
}

function SortButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<SortIcon sx={{ fontSize: 18 }} />}
      sx={{
        flexShrink: 0,
        borderRadius: '999px',
        textTransform: 'none',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: 'text.secondary',
        border: '1px solid #E2E8F0',
        bgcolor: '#F8FAFC',
        px: 1.75,
        py: 0.65,
        minHeight: 34,
        whiteSpace: 'nowrap',
        gap: 0.75,
        '&:hover': { bgcolor: '#F0F9FF', borderColor: '#93C5FD', color: '#3B82F6' },
        '& .MuiButton-startIcon': { margin: 0 },
      }}
    >
      {label}
    </Button>
  );
}

const STATUS_ACCENT: Partial<Record<TaskStatusTab, string>> = {
  not_started: WORK_STATUS_COLORS.not_started.color,
  in_progress: WORK_STATUS_COLORS.in_progress.color,
  blocked: WORK_STATUS_COLORS.blocked.color,
  on_hold: WORK_STATUS_COLORS.on_hold.color,
  completed: WORK_STATUS_COLORS.completed.color,
};

function FilterMenuButton({ group }: { group: FilterGroup }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const selected = group.options.find(o => o.id === group.value);
  const isActive = !!group.value;

  return (
    <>
      <Button
        size="small"
        onClick={e => setAnchor(e.currentTarget)}
        endIcon={
          group.loading
            ? <CircularProgress size={12} />
            : <ArrowDown2 size={14} color={isActive ? '#3B82F6' : '#94A3B8'} />
        }
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: isActive ? 600 : 500,
          px: 1.75,
          py: 0.65,
          minHeight: 34,
          color: isActive ? '#1D4ED8' : 'text.secondary',
          bgcolor: isActive ? '#EFF6FF' : '#F8FAFC',
          border: '1px solid',
          borderColor: isActive ? '#BFDBFE' : '#E2E8F0',
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: isActive ? '#DBEAFE' : '#F1F5F9', borderColor: isActive ? '#93C5FD' : '#CBD5E1' },
        }}
      >
        {isActive ? selected?.label : group.label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 180, mt: 0.5, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' } } }}
      >
        <MenuItem
          selected={!group.value}
          onClick={() => { group.onChange(''); setAnchor(null); }}
          sx={{ fontSize: '0.875rem' }}
        >
          {group.allLabel}
        </MenuItem>
        {group.options.map(opt => (
          <MenuItem
            key={opt.id}
            selected={group.value === opt.id}
            onClick={() => { group.onChange(opt.id); setAnchor(null); }}
            sx={{ fontSize: '0.875rem' }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function TasksToolbar({
  searchValue,
  searchPlaceholder,
  onSearchChange,
  filterGroups,
  activeChips,
  onClearAll,
  filteredCount,
  totalCount,
  statusTabs = [],
  activeTab,
  onTabChange,
  sortLabel,
  onSortToggle,
  clearFiltersLabel,
  showingCountLabel,
}: TasksToolbarProps) {
  const { t } = useTranslation();
  const visibleFilters = filterGroups.filter(g => !g.hidden && g.options.length > 0);
  const hasActiveChips = activeChips.length > 0;
  const showStatusTabs = statusTabs.length > 0 && activeTab !== undefined && onTabChange;
  const showSort = !!sortLabel && !!onSortToggle;

  return (
    <Card sx={{ mb: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        {/* Search + Filters — mirrors automatically: AR search right / EN search left */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchNormal1 size={18} color="#94A3B8" />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: '#F8FAFC',
                '& fieldset': { borderColor: '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#CBD5E1' },
                '&.Mui-focused fieldset': { borderColor: '#93C5FD' },
              },
            }}
          />
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            flexWrap: 'wrap',
            marginInlineStart: 'auto',
          }}>
            {visibleFilters.map(group => (
              <FilterMenuButton key={group.key} group={group} />
            ))}
            {showSort && <SortButton label={sortLabel!} onClick={onSortToggle!} />}
          </Box>
        </Box>

        {showStatusTabs && (
          <>
        <Divider sx={{ my: 1.75, borderColor: '#F1F5F9' }} />

        {/* Status segmented control */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: '#F1F5F9',
            borderRadius: 2.5,
            p: 0.5,
            gap: 0.5,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {statusTabs.map(tab => {
            const selected = activeTab === tab.key;
            const accent = STATUS_ACCENT[tab.key];
            return (
              <Box
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                sx={{
                  flex: { xs: '0 0 auto', sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.85,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  bgcolor: selected ? '#fff' : 'transparent',
                  boxShadow: selected ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                  '&:hover': { bgcolor: selected ? '#fff' : '#E8EDF3' },
                }}
              >
                <Typography sx={{
                  fontSize: '0.8125rem',
                  fontWeight: selected ? 600 : 500,
                  color: selected ? (accent ?? '#3B82F6') : 'text.secondary',
                  whiteSpace: 'nowrap',
                }}>
                  {tab.label}
                </Typography>
                <Box sx={{
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 1,
                  minWidth: 22,
                  textAlign: 'center',
                  bgcolor: selected ? (accent ? `${accent}18` : '#EFF6FF') : '#E2E8F0',
                }}>
                  <Typography sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: selected ? (accent ?? '#3B82F6') : '#64748B',
                  }}>
                    {tab.count}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
          </>
        )}

        {/* Active filter chips */}
        {hasActiveChips && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            {activeChips.map(chip => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                onDelete={chip.onRemove}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 28,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  bgcolor: '#EFF6FF',
                  color: '#1D4ED8',
                  border: '1px solid #BFDBFE',
                  '& .MuiChip-deleteIcon': { color: '#60A5FA', '&:hover': { color: '#3B82F6' } },
                }}
              />
            ))}
            <Button
              size="small"
              onClick={onClearAll}
              sx={{ fontSize: '0.75rem', color: '#EF4444', minWidth: 'auto', px: 1 }}
            >
              {clearFiltersLabel ?? t('tasks.clearFilters')}
            </Button>
          </Box>
        )}

        {/* Result count */}
        {filteredCount !== undefined && totalCount !== undefined && (
          <Typography sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mt: hasActiveChips ? 1.5 : 0,
            pt: hasActiveChips ? 0 : 1.5,
            textAlign: 'start',
          }}>
            {t(showingCountLabel ?? 'tasks.showingCount', { count: filteredCount, total: totalCount })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
